import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ChangeEvent } from 'react';
import { TypeId } from 'src_common/common/websocket2/type';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { UpdateMultiSelectionParams, UpdateMultiSelection } from 'src/AppState/sdk/staff';
import { CriteriaType, isCriteria } from 'src_common/utils/sport/sort';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';

const selectionPercentage = (price: number | undefined, termsReduction: string | undefined): number => {
    if (price === undefined || termsReduction === undefined) {
        return 0;
    }

    const placeReduction = parseInt(termsReduction.slice(2), 10);

    return (1 / ((price - 1) / placeReduction + 1)) * 100;
};

const selectionPercentageForMargin = (price: string | undefined): number => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!price) {
        return 0;
    }
    const splitPrice = price.split('/');
    const numerator = parseInt(splitPrice[0] === undefined ? '0' : splitPrice[0], 10);
    const denominator = parseInt(splitPrice[1] === undefined ? '1' : splitPrice[1], 10);
    return (1 / (numerator / denominator + 1)) * 100;
};

export class MarketSettingsFormState {
    @observable public sortOrder: CriteriaType;
    @observable public currentSelectionActive: boolean;
    @observable public currentSelectionsDisplay: boolean;

    public constructor(
        private readonly appState: AppState,
        private readonly marketId: MarketId | null
        // private readonly props: MarketSettingsFormPropsType
    ) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this.sortOrder = (this.appState.routerBackoffice.getParameter('events', 'sortOrder') ?? '-') as CriteriaType;
        this.currentSelectionActive = this.isAnySelectionActive;
        this.currentSelectionsDisplay = this.isAnySelectionDisplay;
        makeAutoObservable(this);
    }

    @action public onSortOrderChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;

        if (isCriteria(value)) {
            this.appState.routerBackoffice.setParamInNamespace('events', 'sortOrder', value);
            this.sortOrder = value;
        } else {
            console.error('onSortOrderChange wrong value in select');
        }
    };

    @computed public get placeMargin(): number {
        if (this.marketId === null) {
            return 0;
        }

        const marketModel = this.marketId.getModel();

        if (marketModel === null) {
            console.warn('Market Model is null;');
            return 0;
        }

        const marketSelections = marketModel.selections;

        const isEachWayAndPlaceReduction =
            marketModel.eachWayOffered === true && marketModel.eachWayTermsReduction !== undefined;

        let totalPercentage = 0;

        if (isEachWayAndPlaceReduction === true) {
            marketSelections.forEach(
                (selection) =>
                    (totalPercentage += selectionPercentage(selection.price?.d, marketModel.eachWayTermsReduction))
            );
        }

        return Math.round(totalPercentage);
    }

    @computed public get margin(): number {
        let totalPercentage = 0;
        if (this.marketId === null) {
            return 0;
        }

        const marketModel = this.marketId.getModel();

        if (marketModel === null) {
            console.warn('Market Model is null;');
            return 0;
        }

        const marketSelections = marketModel.selections;

        marketSelections.forEach((selection) => (totalPercentage += selectionPercentageForMargin(selection.price?.f)));

        return Math.round(totalPercentage);
    }

    @action public setSelectionOrder = (marketId: MarketId): void => {
        const marketModel = marketId.getModel();
        if (marketModel === null) {
            return;
        }
        const selections = marketModel.selections;
        const out: { selectionsOrder: Record<number, { displayOrder: number }> } = { selectionsOrder: {} };
        selections.forEach((selection, index) => {
            out.selectionsOrder[selection.id] = { displayOrder: ++index };
        });

        setTimeout(async () => {
            this.appState.sdkStaff
                .event_reorder_selection({
                    event_id: marketModel.eventId,
                    market_id: TypeId.getMarketId(marketModel.id),
                    requestBody: out,
                })
                .catch((error) => {
                    console.error(error);
                });
        }, 250);
    };

    @computed public get marketSelections(): Array<SelectionModel> {
        if (this.marketId !== null) {
            const marketModel = this.marketId.getModel();
            return marketModel?.selections ?? [];
        }
        return [];
    }

    @computed public get isAnySelectionDisplay(): boolean {
        return this.marketSelections.filter((selection) => selection.display === true).length > 0;
    }

    @computed public get isAnySelectionActive(): boolean {
        return this.marketSelections.filter((selection) => selection.active === true).length > 0;
    }

    @action public onDisplayAllChange = async (): Promise<void> => {
        const requestData: UpdateMultiSelection = [];
        for (const selection of this.marketSelections) {
            if (selection.display === this.currentSelectionsDisplay) {
                requestData.push({
                    selection_id: selection.id,
                    data: { display: !this.currentSelectionsDisplay },
                });
            }
        }
        await this.updateSelection(requestData);
        this.currentSelectionsDisplay = !this.currentSelectionsDisplay;
    };

    @action public onActiveAllChange = async (): Promise<void> => {
        const requestData: UpdateMultiSelection = [];
        for (const selection of this.marketSelections) {
            if (selection.active === this.currentSelectionActive) {
                requestData.push({
                    selection_id: selection.id,
                    data: { active: !this.currentSelectionActive },
                });
            }
        }
        await this.updateSelection(requestData);
        this.currentSelectionActive = !this.currentSelectionActive;
    };

    @action public updateSelection = async (data: UpdateMultiSelection): Promise<void> => {
        if (this.marketId === null) {
            return;
        }

        const marketModel = this.marketId.getModel();

        if (marketModel === null) {
            return;
        }

        const body: UpdateMultiSelectionParams = {
            event_id: marketModel.eventId,
            market_id: this.marketId.toOldId(),
            requestBody: data,
        };

        const response = await this.appState.sdkStaff.staff_selection_multi_update(body);

        if (response.status === 'error') {
            console.warn('Selection update error response - ', response);
        }
    };
}
